<template>
  <el-dialog destroy-on-close :model-value="visible" :title="title" :width="780" @close="close" @open="open">
    <el-form ref="elFormRef" :label-width="80" :model="formData" :rules="formRules">
      <el-form-item label="岗位名称" prop="name">
        <el-input v-model="formData.name" clearable maxlength="50" placeholder="请输入岗位名称" show-word-limit />
      </el-form-item>
      <el-form-item label="项目编码" prop="outJobCode">
        <el-input v-model="formData.outJobCode" clearable maxlength="10" placeholder="请输入项目编码" show-word-limit />
      </el-form-item>
      <el-form-item label="岗位类型" prop="categoryIds">
        <el-cascader
          v-model="formData.categoryIds"
          filterable
          :options="categoryOptions.filter((e) => e.children.length > 0)"
          placeholder="请选择岗位类型"
          :props="{
            value: 'id',
            label: 'categoryName',
            expandTrigger: 'hover',
          }"
          style="width: 276px"
        />
      </el-form-item>
      <el-form-item label="岗位描述" prop="description">
        <el-input
          v-model="formData.description"
          maxlength="2000"
          placeholder="岗位描述"
          :rows="8"
          show-word-limit
          type="textarea"
        />
      </el-form-item>
      <el-form-item label="特色标签" prop="tagIds">
        <div class="tag-container">
          <span
            v-for="tag in tagList"
            :key="tag.id"
            class="tag-item"
            :class="{ active: formData.tagIds.includes(tag.id) }"
            @click="toggleTag(tag.id, formData.tagIds)"
          >
            {{ tag.tagName }}
          </span>
        </div>
      </el-form-item>
      <el-form-item label="薪资面议" prop="isSalaryNegotiable">
        <el-radio-group v-model="formData.isSalaryNegotiable">
          <el-radio :label="true">是</el-radio>
          <el-radio :label="false">否</el-radio>
        </el-radio-group>
      </el-form-item>
      <template v-if="!formData.isSalaryNegotiable">
        <!-- 结算方式 -->
        <el-row>
          <el-col :span="6">
            <el-form-item label="结算方式" prop="settlementType">
              <el-select v-model="formData.settlementType" style="width: 108px">
                <el-option
                  v-for="item in settlementOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6" style="margin-left: 16px">
            <el-form-item
              v-if="formData.settlementType === SETTLEMENT.MONTH"
              label-width="auto"
              prop="monthSalarySystem"
            >
              <el-input v-model="formData.monthSalarySystem" style="width: 108px">
                <template #suffix>薪</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 薪资范围 -->
        <el-form-item label="薪资范围" prop="salary">
          <el-input v-model="formData.salary[0]" placeholder="下限" style="width: 108px">
            <template #suffix>{{ formData.salaryUnit }}</template>
          </el-input>
          <span style="margin: 0 3px">-</span>
          <el-input v-model="formData.salary[1]" placeholder="上限" style="width: 108px">
            <template #suffix>{{ formData.salaryUnit }}</template>
          </el-input>
        </el-form-item>
      </template>
      <!-- 工作地点 -->
      <el-form-item label="工作地点" prop="workLocationCodes">
        <div class="el-cascader--fix">
          <el-cascader
            v-model="formData.workLocationCodes"
            clearable
            filterable
            :props="locationCascaderProps"
            style="width: 276px"
          />
        </div>
      </el-form-item>
      <el-form-item label="推荐奖励">
        <div class="recommend-tags">
          <el-tag
            v-for="(rule, index) in rewardRules"
            :key="rule.id"
            :closable="!rule.locked"
            :disable-transitions="false"
            size="large"
            @close="removeRewardRule(index)"
          >
            {{ rule.name }}
          </el-tag>
          <el-link v-if="rewardAddable" icon="plus" type="primary" :underline="false" @click="incentiveModal.open()">
            添加推荐奖励
          </el-link>
        </div>
      </el-form-item>
      <el-form-item v-if="type === 'add'" label="岗位状态" prop="status">
        <el-select v-model="formData.status">
          <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="close">取消</el-button>
      <el-button type="primary" @click="confirm">确定</el-button>
    </template>

    <el-dialog
      v-model="incentiveModal.visible"
      destroy-on-close
      title="选择推荐奖励"
      :width="980"
      @close="cancelRewardChoose"
    >
      <Incentive :reward-rules="rewardRules" selection @additional-change="handleRewardsChange" />
      <template #footer>
        <el-button @click="cancelRewardChoose">取消</el-button>
        <el-button type="primary" @click="confirmRewards">确定</el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script setup>
  import {
    ref,
    toRaw,
    reactive,
    computed,
    onMounted,
    defineEmits,
    defineProps,
    watchEffect,
    getCurrentInstance,
  } from 'vue'
  import { JOB_STATUS, JOB_STATUS_CN, SETTLEMENT, SETTLEMENT_CN, SALARY_UNIT, SALARY_SCALE } from '../context'
  import { getProvinceInfo, getCityInfo, getAreaInfo } from '@/api/shop'
  import { last, omit, uniq } from 'lodash'
  import { addJob, getTagList } from '@/api/recruitment'
  import Incentive from '@/views/recruitment/incentive'
  import { useModal } from '@/hooks/useModal'

  const props = defineProps({
    type: String,
    visible: Boolean,
    categoryOptions: Array,
    data: Object,
  })

  const emit = defineEmits(['success', 'close'])

  const title = computed(() => `${props.type === 'add' ? '添加' : '编辑'}岗位`)

  const { proxy } = getCurrentInstance()

  const elFormRef = ref()

  const tagList = ref([])

  onMounted(() => {
    getTagList().then(({ data }) => (tagList.value = data))
  })

  const settlementOptions = Object.entries(SETTLEMENT_CN).map(([k, v]) => ({
    value: k,
    label: v,
  }))

  const statusOptions = Object.entries(JOB_STATUS_CN).map(([k, v]) => ({
    value: k,
    label: v,
  }))

  const locationMap = {}
  const setLocation = ({ code, name }) => (locationMap[code] = name)
  const locationCascaderProps = {
    lazy: true,
    value: 'code',
    label: 'name',
    multiple: true,
    checkStrictly: true,
    expandTrigger: 'hover',
    async lazyLoad(node, resolve) {
      const { level } = node
      const { code } = node.data
      if (level === 0) {
        const { data } = await getProvinceInfo()
        data.forEach((v) => {
          v.disabled = !v.name.endsWith('市')
          setLocation(v)
        })
        resolve(data)
      } else if (level === 1) {
        const { data } = await getCityInfo({ code })
        data.forEach((v) => {
          v.disabled = v.name === '直辖市'
          setLocation(v)
        })
        resolve(data)
      } else {
        const { data } = await getAreaInfo({ code })
        data.forEach((v) => {
          v.leaf = level >= 2
          setLocation(v)
        })
        resolve(data)
      }
    },
  }

  const defaultSalaryFormData = {
    minSalary: '',
    maxSalary: '',
    monthSalarySystem: '12', // 月结薪资体系 12-99
    salaryUnit: SALARY_UNIT[SETTLEMENT.MONTH], // 薪资单位
    settlementType: SETTLEMENT.MONTH, // 结算方式
  }

  const defaultFormData = {
    name: '', // 岗位名称
    outJobCode: '', // 项目编码
    jobId: '', // 岗位ID
    categoryId: '', // 岗位类型ID
    categoryIds: [], // 岗位类型ID
    description: '', // 岗位描述
    tagIds: [], // 特色标签
    isSalaryNegotiable: false, // 是否薪资面议
    status: JOB_STATUS.UN_PUBLISHED, // 岗位状态
    workLocationCodes: [], // 工作地点的 code
    workLocations: [], // 工作地点
    salary: [], // 薪资范围
    ruleId: [], // 奖励规则ID
    ...defaultSalaryFormData,
  }

  const formData = reactive(structuredClone(defaultFormData))

  const formRules = {
    name: [{ required: true, message: '请输入岗位名称' }],
    description: [{ required: true, message: '请输入岗位描述' }],
    categoryIds: [{ type: 'array', required: true, message: '请选择岗位类型' }],
    salary: [
      {
        type: 'array',
        required: true,
        len: 2,
        message: '请输入完整的薪资范围',
      },
      {
        validator(rule, [min, max], callback) {
          min = +min
          max = +max
          let msg = ''
          if (isNaN(min)) msg = '薪资下限必须是一个数字'
          else if (isNaN(max)) msg = '薪资上限必须是一个数字'
          else if (min <= 0) msg = '薪资下限必须大于0'
          else if (min > max) msg = '薪资上限不得小于薪资下限'
          callback(msg ? new Error(msg) : void 0)
        },
      },
    ],
    workLocationCodes: [
      {
        type: 'array',
        required: true,
        message: '请选择工作地点',
        trigger: 'change',
      },
    ],
    monthSalarySystem: [
      {
        validator(rule, value, callback) {
          if (12 <= value && value <= 99) callback()
          else callback(new Error('请输入12-99 以内的数字'))
        },
      },
    ],
    status: [{ required: true, message: '请选择岗位状态' }],
  }

  watchEffect(() => {
    formData.categoryId = last(formData.categoryIds)
  })

  watchEffect(() => {
    formData.salaryUnit = SALARY_UNIT[formData.settlementType]
    const scale = SALARY_SCALE[formData.settlementType]
    const [min, max] = formData.salary
    if (min) formData.minSalary = min * scale
    if (max) formData.maxSalary = max * scale
  })

  watchEffect(() => {
    formData.workLocations = formData.workLocationCodes.map(([provinceCode, cityCode, areaCode]) => {
      const obj = {}
      if (provinceCode) (obj.provinceCode = provinceCode), (obj.province = obj.locationName = locationMap[provinceCode])
      if (cityCode) (obj.cityCode = cityCode), (obj.city = obj.locationName = locationMap[cityCode])
      if (areaCode) (obj.areaCode = areaCode), (obj.area = obj.locationName = locationMap[areaCode])
      return obj
    })
  })

  function open() {
    const isEdit = props.type === 'edit' && props.data
    setFormByData(isEdit ? props.data : structuredClone(defaultFormData))
    rewardRules.value = isEdit ? (props.data.rewardRulesVos || []).map((e) => ({ ...e, locked: true })) : []
  }

  function setFormByData(data) {
    for (const key in data) {
      const val = data[key]
      if (key === 'id') {
        formData.jobId = val
      } else if (key === 'category') {
        formData.categoryIds = val.map((v) => v.id)
      } else if (key === 'workLocations' && Array.isArray(val)) {
        const list = new Set()
        val.forEach(({ provinceCode, cityCode, areaCode }) => {
          const arr = []
          if (provinceCode) arr.push(+provinceCode)
          if (cityCode) arr.push(+cityCode)
          if (areaCode) arr.push(+areaCode)
          list.add(arr.toString())
        })
        formData.workLocationCodes = [...list].map((e) => e.split(',').map((v) => +v))
      } else if (key === 'enterpriseJobTagVos') {
        formData.tagIds = (val || []).filter((v) => !!v).map((v) => v.id)
      } else if (key in formData) {
        formData[key] = val
      }
    }
    formData.salary = [data.minSalary, data.maxSalary].map((v) => v / SALARY_SCALE[data.settlementType])
  }

  function confirm() {
    elFormRef.value.validate((valid) => {
      if (!valid) return

      let params = omit(toRaw(formData), 'categoryIds', 'workLocationCodes', 'salary')

      if (formData.isSalaryNegotiable) params = { ...params, ...defaultSalaryFormData }
      else if (formData.settlementType !== SETTLEMENT['MONTH']) params.monthSalarySystem = '12'

      addJob(params).then((res) => {
        if (res.code !== 200) return
        proxy.$baseMessage(`${title.value}成功！`, 'success', 'vab-hey-message-success')
        emit('success')
      })
    })
  }

  function close() {
    emit('close')
  }

  function toggleTag(tag, list) {
    const index = list.indexOf(tag)
    if (index > -1) list.splice(index, 1)
    else if (formData.tagIds.length >= 5) proxy.$baseMessage('最多选择5个标签！', 'warning', 'vab-hey-message-warn')
    else list.push(tag)
  }

  const rewardRules = ref([])
  const additionalRewards = ref([])
  const incentiveModal = useModal()
  const rewardAddable = computed(() => !props.data?.rewardRulesVos?.length)

  function handleRewardsChange(rows) {
    additionalRewards.value = rows
  }

  function confirmRewards() {
    rewardRules.value = uniq(rewardRules.value.concat(additionalRewards.value))
    formData.ruleId = rewardRules.value.map((e) => e.id)
    additionalRewards.value = []
    incentiveModal.close()
  }

  function cancelRewardChoose() {
    additionalRewards.value = []
    incentiveModal.close()
  }

  function removeRewardRule(index) {
    rewardRules.value.splice(index, 1)
  }
</script>

<style lang="scss" scoped>
  .el-cascader--fix :deep(.el-cascader__search-input:not(:only-child)) {
    display: none;
  }

  .tag-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 0;
  }

  .tag-item {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    color: #909399;
    background-color: #fff;
    border: 1px solid rgba(199.5, 201, 204);
    transition: all 0.3s;
    font-size: 12px;
    line-height: 16px;
    margin: 0;

    &:hover,
    &.active {
      color: var(--el-color-primary);
      border-color: var(--el-color-primary-light-8);
    }

    &.active {
      background-color: var(--el-color-primary-light-9);
    }
  }

  .recommend-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 2px;

    :deep(.el-tag) {
      padding: 0 8px;
      height: 28px;
      line-height: 26px;
    }

    :deep(.el-tag + .el-tag) {
      margin: 0;
    }

    :deep(.el-link) {
      height: 28px;
      line-height: 28px;

      &,
      > .el-icon {
        font-size: 12px;
      }
    }
  }
</style>
